import type { en } from './en'
import type { Forms } from './plural'

export const ru: typeof en = {
  common: {
    loading: 'Загрузка…',
    failed: 'Не вышло',
    retry: 'Повторить',
    reload: 'Обновить',
    done: 'Готово',
    next: 'Дальше',
    back: 'Назад',
    finish: 'Завершить',
    cancel: 'Отмена',
    clear: 'Очистить',
    of: 'из',
    optional: 'необязательно',
    nothingFound: 'Ничего не найдено',
    unknown: 'неизвестно',
    notFoundTitle: 'Страница не найдена',
    notFoundHint: 'Ссылка ведёт в никуда.',
    error: (status: number) =>
      status === 404
        ? 'Ничего не нашлось'
        : status === 401 || status === 403
          ? 'Нет доступа'
          : `Не прошло, ошибка ${status}`,
  },

  units: {
    items: ['пункт', 'пункта', 'пунктов'] as Forms,
    updates: ['обновление', 'обновления', 'обновлений'] as Forms,
    actions: ['действие', 'действия', 'действий'] as Forms,
    products: ['средство', 'средства', 'средств'] as Forms,
    days: ['день', 'дня', 'дней'] as Forms,
    weeks: ['неделя', 'недели', 'недель'] as Forms,
    components: ['компонент', 'компонента', 'компонентов'] as Forms,
    expires: ['истекает', 'истекают', 'истекают'] as Forms,
    photos: ['фотография', 'фотографии', 'фотографий'] as Forms,
    uses: ['применение', 'применения', 'применений'] as Forms,
    steps: ['шаг', 'шага', 'шагов'] as Forms,
  },

  nav: {
    today: 'Сегодня',
    week: 'Неделя',
    products: 'Средства',
    spf: 'SPF',
    more: 'Ещё',
  },

  today: {
    title: 'Сегодня',
    stepsDone: 'шагов сделано',
    emptyTitle: 'На сегодня пусто',
    emptyHint: 'Шаги появятся, когда ИИ пришлёт схему.',
    skipReasons: [
      { value: 'forgot', label: 'Забыл' },
      { value: 'skin irritated', label: 'Кожа раздражена' },
      { value: 'ran out', label: 'Закончилось' },
      { value: 'no time', label: 'Не было времени' },
      { value: 'chose not to', label: 'Решил не наносить' },
    ],
    ownReason: 'chose not to',
    ownReasonLabel: 'Почему нет',
    ownReasonTags: ['липкость', 'лень', 'спешка', 'запах', 'коже и так хорошо'],
    skipTitle: 'Пропустить шаг',
    skipShort: 'Пропуск',
    skipReasonLabel: 'Причина',
    skipSave: 'Сохранить пропуск',
    clearMark: 'Сбросить',
    stung: 'Сегодня щипало',
    stungClear: 'Не щипало',
    stungMark: 'щипало',
    openProduct: 'Открыть карточку средства',
    ownReasonPlaceholder: 'своими словами — необязательно',
    more: 'Ещё',
  },

  week: {
    title: 'Неделя',
    today: 'сегодня',
    noData: 'нет данных',
    full: 'полностью',
    partial: 'частично',
  },

  spf: {
    title: 'SPF',
    applied: 'Нанесено',
    notAppliedYet: 'Ещё не нанесено',
    notMarkedToday: 'Сегодня не отмечено',
    noUpdates: 'без обновлений',
    dayGoesOn: 'день продолжается',
    apply: 'Отметить нанесение',
    unapply: 'Сбросить',
    reapply: 'Отметить обновление',
    marked: 'SPF отмечен',
    monthLabel: 'в этом месяце',
    notMarked: 'Не отмечено',
    todayIs: (date: string) => `сегодня, ${date}`,
    withApplication: 'с нанесением',
    exposureTitle: 'Где вы сегодня будете?',
    exposureIndoors: 'В помещении',
    exposureWindow: 'У окна',
    exposureOutdoors: 'На улице',
    exposureHint: {
      indoors:
        'Одного утреннего слоя хватит на день: два пальца на лицо и шею, обновлять не нужно.',
      window:
        'Закрытое окно пропускает длинный UVA. Слой утром и второй после обеда, столько же — два пальца.',
      outdoors:
        'На улице слой держится около двух часов: обновлять каждые два часа и после воды, по два пальца, каждое обновление отмечать здесь.',
    } as Record<string, string>,
    exposureShort: {
      indoors: 'в помещении',
      window: 'у окна',
      outdoors: 'на улице',
    } as Record<string, string>,
    byWindow: 'из них у окна',
    reappliedOn: (days: string) => `обновлено в ${days}`,
    legendNone: 'не отмечено',
    legendApplied: 'нанесено',
    legendReapplied: 'с обновлением',
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
  },

  products: {
    title: 'Средства',
    searchPlaceholder: 'Название, бренд или состав',
    clearSearch: 'Очистить поиск',
    inRoutine: 'в схеме',
    emptyNoRoutine: 'Средства появятся, когда ИИ пришлёт схему.',
    emptyTryAnother: 'Попробуйте другой запрос или снимите фильтр.',
    all: 'Все',
    routine: 'В схеме',
    shelf: 'На полке',
    noSlot: 'Вне схемы',
    order: 'Порядок',
    orderLabel: 'Порядок списка',
    orderFlat: 'одним списком',
    orderHeaded: 'с заголовками',
    orderName: 'По алфавиту',
    orderRemaining: 'Сначала заканчивается',
    orderSlot: 'По слотам',
    orderStatus: 'По статусу',
  },

  product: {
    purpose: 'зачем',
    inRoutine: 'в схеме',
    notInRoutine: 'Не в текущей схеме — лежит на полке.',
    noInci: 'Не заполнен. Разбор активных появится вместе с ним.',
    stockUnknown: 'Неизвестен. Расход считается от первой отметки остатка.',
    noNotes: 'Пусто.',
    stock: 'остаток',
    expires: (date: string) => `годно до ${date}`,
    expiryUncertain: 'срок годности под вопросом',
    usedSince: (uses: string, date: string) => `${uses} с ${date}.`,
    usedSinceLeft: (uses: string, date: string, left: number) =>
      `${uses} с ${date}, осталось ~${left}.`,
    lastsAbout: (weeks: string) => `При таком темпе хватит ещё примерно на ${weeks}.`,
    actives: 'активные',
    fullInci: 'полный INCI',
    collapse: 'Свернуть',
    expand: 'Развернуть',
    composition: 'состав',
    preservatives: 'консерванты',
    afterPreservative: 'после консерванта',
    traceHint: 'Ниже черты — следовые концентрации.',
    expectedEffect: 'ожидаемый эффект',
    evaluateAfter: (weeks: string) => `оценивать после ${weeks} применения`,
    notes: 'заметки',
  },

  flags: {
    kinds: {
      'severe-reaction': 'Сильная реакция',
      worsening: 'Становится хуже',
      'spf-gap': 'Нет SPF',
    } as Record<string, string>,
  },

  conflicts: {
    myth: 'миф',
  },

  checkin: {
    title: 'Чек-ин',
    doneTitle: 'Чек-ин пройден',
    doneSubtitle: 'Ответы уходят в выгрузку как есть',
    saved: 'Чек-ин сохранён',
    yes: 'да',
    no: 'нет',
    textPlaceholder: 'Своими словами',
    eyebrow: (type: string, index: number, total: number, multi: boolean) =>
      `${type} чек-ин, ${index} из ${total}${multi ? ', можно несколько' : ''}`,
  },

  reaction: {
    title: 'Реакция',
    steps: ['Что случилось?', 'Где?', 'Насколько сильно?', 'На что думаете?', 'Что сделали?'],
    eyebrow: (step: number, total: number) => `реакция, ${step} из ${total}`,
    dontKnow: 'не знаю',
    actionPlaceholder: 'Смыл, нанёс крем, отменил средство…',
    record: 'Записать',
    recorded: 'Реакция записана',
    zones: ['лоб', 'нос', 'щёки', 'подбородок', 'шея', 'везде'],
  },

  history: {
    title: 'История',
    emptyTitle: 'Пока пусто',
    emptyHint: 'Первая отметка на «Сегодня» откроет этот журнал.',
    reaction: 'реакция',
    checkin: 'чек-ин',
    morning: 'утро',
    evening: 'вечер',
    whole: 'целиком',
    spfApplied: 'SPF нанесён',
    spfNotMarked: 'SPF не отмечен',
    routineEdited: 'схема изменена',
    nothingMarked: 'ничего не отмечено',
    skipped: (product: string, reason: string) => `${product} пропущено — ${reason}`,
  },

  historyDay: {
    alsoThatDay: 'в тот же день',
    reactionRecorded: 'Записана реакция',
    suspected: (product: string) => `, подозрение на ${product}`,
    weeklyCheckin: 'Недельный чек-ин',
    monthlyCheckin: 'Месячный чек-ин',
    routine: 'Схема',
    perDay: 'за день',
    exposure: (place: string) => `, ${place}`,
  },

  export: {
    title: 'Выгрузка',
    adherence: 'Соблюдение схемы',
    checkinAnswers: 'Ответы чек-инов',
    reactions: 'Реакции',
    productsAndUse: 'Средства и применение',
    expiry: 'Сроки годности',
    expiringSuffix: 'в ближайшие три месяца',
    conflicts: 'Найденные конфликты',
    consultant: 'ИИ-консультант',
    consultantConnected: 'Он забирает эти факты сам, отправлять ничего не нужно.',
    consultantMissing: 'Не подключён. Ссылка выдаётся в разделе «Подключение».',
    files: 'файлы',
    backupTitle: 'Резервная копия',
    backupHint: 'Все данные приложения, чтобы вернуть их позже.',
    saveBackup: 'Сохранить копию',
    restoreAction: 'Восстановить из файла',
    consultantFile: 'Выгрузка для консультанта',
    consultantFileHint:
      'Файл для любого ИИ-ассистента: приложите его к чату вместе с промптом. Нужен, когда коннектора нет.',
    saveFile: 'Сохранить файл',
    saved: 'Загрузка открылась в браузере',
    failed: 'Выгрузка не удалась',
    restoreTitle: 'Восстановление из файла',
    restoreReplaces: 'Все текущие данные будут удалены и заменены содержимым файла.',
    restoreConfirm: 'Заменить данные',
    restoreCancel: 'Отмена',
    restoreBad: 'Это не резервная копия nitea',
    restoreDone: 'Данные восстановлены из файла',
    restoreFailed: 'Восстановить не удалось',
    fileProducts: 'Средства',
    fileMarks: 'Отметки схемы',
    fileCheckins: 'Чек-ины',
    filePeriod: 'Период',
    fileVersion: 'Версия схемы',
    footnote: 'Без фотографий и ключей. Отметки только за этот период.',
  },

  more: {
    title: 'Ещё',
    routine: 'схема',
    records: 'записи',
    settings: 'настройки',
    attentionHint: 'Что истекает, что закончилось, что отметил ИИ',
    notesHint: 'Что записал ИИ и какие мифы разобрал',
    checkinDone: 'Пройден на этой неделе',
    checkinStarted: 'Начат',
    reactionHint: 'Записать эпизод: что, где, насколько сильно и что сделали',
    historyHint: 'Журнал по дням',
    exportHint: 'Факты за период',
    settingsHint: 'Напоминания, еженедельный чек-ин, часовой пояс',
    connectionHint: 'Ссылка для ИИ-консультанта, права и последние события',
    photos: 'Фотографии',
    photosHint: 'Кадры по ракурсам',
  },

  photos: {
    title: 'Фотографии',
    angleLabel: 'ракурс',
    add: 'Добавить фотографию',
    earlier: 'раньше',
    uploading: 'Загрузка…',
    delete: 'Удалить фотографию',
    deleteConfirm: 'Удалить насовсем?',
    closeViewer: 'Закрыть фотографию',
    closeCompare: 'Закрыть сравнение',
    compareAction: 'Сравнить',
    compareLeft: 'слева от ручки',
    compareRight: 'справа от ручки',
    compareLeftTitle: 'Левая фотография',
    compareRightTitle: 'Правая фотография',
    emptyTitle: 'Фотографий пока нет',
    emptyHint: 'Сделайте первую — следующая покажет сравнение с ней.',
    previous: 'предыдущая фотография',
    latest: 'последняя фотография',
    sourceTitle: 'Добавить фотографию',
    sourceHint: 'Камера выровняет новый кадр по предыдущему.',
    camera: 'Сфотографировать',
    cameraFallback: 'Выбрать файл вместо камеры',
    cameraHint: 'На этом устройстве нет камеры, доступной приложению.',
    cameraFailed: 'Камера не открылась',
    gallery: 'Выбрать файл',
    pose: {
      front: 'Смотрите в камеру, глаза на пунктирной линии',
      left: 'Поверните голову, пока левая щека не заполнит овал',
      right: 'Поверните голову, пока правая щека не заполнит овал',
      detail: 'Выведите участок в середину кадра',
    },
    compare: 'Показать предыдущую фотографию',
    comparing: 'выровнено по',
    shutter: 'Сфотографировать',
    flip: 'Другая камера',
    closeCamera: 'Закрыть камеру',
    keepTitle: 'Оставить этот кадр?',
    keepHint: 'предыдущий:',
    retake: 'Переснять',
    save: 'Сохранить фотографию',
  },

  time: {
    digits: 'Цифры',
    wheels: 'Барабаны',
    badFormat: 'Не похоже на время',
    later: 'Позже',
    earlier: 'Раньше',
  },

  settings: {
    title: 'Настройки',
    language: 'язык',
    languageNote: 'Для переключения приложение перезагрузится.',
    timezone: 'часовой пояс',
    timezoneTitle: 'Часовой пояс',
    timezoneSearch: 'Город или смещение',
    timezoneEmpty: 'Попробуйте другой город или смещение — например «+4» или «Дубай».',
    timezoneFromSystem: 'из системы',
    regions: {
      Europe: 'Европа',
      Asia: 'Азия',
      America: 'Америка',
      Australia: 'Австралия',
      Africa: 'Африка',
      Atlantic: 'Атлантика',
      Indian: 'Индийский океан',
      Antarctica: 'Антарктида',
      Arctic: 'Арктика',
      Pacific: 'Тихий океан',
      UTC: 'UTC',
    } as Record<string, string>,
    timezoneHint: 'День и статистика считаются в этом поясе.',
    timezoneSystem: 'системный',
    timezoneManual: 'изменён вручную',
    weeklyCheckin: 'недельный чек-ин',
    day: 'день',
    time: 'время',
    monthlyDay: 'ежемесячно, число',
    monthlyDayField: 'число месяца',
    monthlyDayNote: (day: string) => `${day} числа каждого месяца`,
    monthlyDayEdge: '28-е — край: в некоторых месяцах более поздней даты нет',
    morningReminder: 'утреннее напоминание',
    morningReminderLabel: 'Напоминать об утренней схеме',
    reminderNote: 'Выключено по умолчанию. Бот ждёт «ок», иначе повторит через час.',
    eveningReminder: 'вечернее напоминание',
    eveningReminderLabel: 'Напоминать о вечерней схеме',
    aiReview: 'разбор с ИИ',
    aiReviewLabel: 'Напоминать показать уход ИИ',
    aiReviewNote: 'Придёт, только если ИИ месяц не читал записи.',
    spfReminder: 'SPF',
    spfReminderNote:
      'Пока в схеме есть кислоты или ретиноид, бот напоминает про SPF через два часа после утреннего времени. Это не настройка.',
    profile: 'профиль кожи',
    profileEmpty: 'Ничего не записано. ИИ-клиент заполняет это из того, что вы ему рассказали.',
    profileUpdated: (date: string) => `Записано ${date}`,
    profileSkinType: 'Тип кожи',
    profileConditions: 'Состояния',
    profileConcerns: 'Работаем над',
    profileIntolerances: 'Реагирует на',
    profileMedications: 'Препараты',
    aiPhotos: 'фотографии для ИИ-клиента',
    aiPhotosLabel: 'Разрешить ключу подключения видеть фотографии',
    aiPhotosNote:
      'Закрыто по умолчанию. Пока открыто, ИИ-клиент видит список кадров и открывает каждый по подписанной ссылке, и ни одна ссылка не живёт дольше окна. Загрузка и удаление остаются в приложении.',
    aiPhotosOff: 'Закрыто',
    aiPhotosHour: '1 час',
    aiPhotosDay: '24 часа',
    aiPhotosAlways: 'Всегда',
    aiPhotosUntil: (moment: string) => `открыто до ${moment}`,
    aiPhotosAlwaysNote: 'Открыто, пока вы не закроете.',
    weekdayField: 'день недели для чек-ина',
    weekdays: [
      { value: 1, label: 'Пн', weekend: false },
      { value: 2, label: 'Вт', weekend: false },
      { value: 3, label: 'Ср', weekend: false },
      { value: 4, label: 'Чт', weekend: false },
      { value: 5, label: 'Пт', weekend: false },
      { value: 6, label: 'Сб', weekend: true },
      { value: 0, label: 'Вс', weekend: true },
    ],
    weekdayEvery: [
      'каждое воскресенье',
      'каждый понедельник',
      'каждый вторник',
      'каждую среду',
      'каждый четверг',
      'каждую пятницу',
      'каждую субботу',
    ] as string[],
    hour: 'часы',
    minute: 'минуты',
    less: 'Меньше',
    more: 'Больше',
  },

  connection: {
    title: 'Подключение',

    yourLinkHint:
      'Один раз вставьте её в Claude или ChatGPT как коннектор. Работает, пока не замените.',
    copyLink: 'Скопировать ссылку',
    showKey: 'Показать ключ',
    hideKey: 'Скрыть ключ',
    replace: 'Заменить',
    linkIsKey: 'Ссылка и есть ключ. Переслав её, вы отдаёте право писать в ваши записи.',
    hiddenOnLeave: 'Снова скроется, когда вы уйдёте с экрана.',
    noLinkYet: 'Ключа пока нет. Получите его и вставьте в AI-клиент как коннектор.',
    clientName: 'ИИ-консультант',
    copy: 'Скопировать',
    copied: 'Ссылка скопирована',
    noKey: 'Ключ ещё не выдан',
    issue: 'Получить ссылку',
    revoke: 'Отозвать',
    revoked: 'Ключ отозван',
    revokeNote: 'Данные остаются — отзывается только доступ.',
    permissions: 'что может ИИ',
    keys: 'ключ',
    events: 'последние события',
    inUse: 'используется',
    notUsedYet: 'ещё не использовался',
    since: 'выдан',
    statusNotConfigured: 'MCP не настроен',
    statusConnected: 'Подключён',
    statusWaiting: 'Ждём ИИ',
    statusDisconnected: 'Не подключён',
    keyUsed: 'ключ уже использован',
    linkIssued: 'ссылка выдана',
    canWrite: 'Присылать схему',
    canRead: 'Читать факты за период',
    canSeeReactions: 'Видеть реакции и флаги',
    photos: 'Фотографии',
    write: 'запись',
    read: 'чтение',
    forbidden: 'запрещено',
  },

  onboarding: {
    title: 'Пока нечего делать',
    hint: 'Шаги появятся, когда ИИ пришлёт уход. До этого три вещи.',
    stepLink: 'Скопируйте ссылку',
    stepLinkHint: 'Это ключ к записям. Никому не пересылайте.',
    copyLink: 'Скопировать ссылку',
    stepConnector: 'Добавьте её как коннектор',
    stepMessage: 'Отправьте первое сообщение',
    stepMessageHint:
      'Готовый промпт: он говорит ассистенту, о чём спросить и что записать. Вставьте его и ответьте на вопросы.',
    copyMessage: 'Скопировать',
    read: 'Прочитать',
    leaves: 'Карточка исчезнет сама, когда придёт первый шаг.',
    messageTitle: 'Первое сообщение',
    messageSubtitle: 'Вставьте в чат, где добавлен коннектор.',
    messageCopied: 'Первое сообщение скопировано',
    close: 'Закрыть',
    keptTitle: 'Первое сообщение',
    keptHint: 'Лежит рядом со ссылкой, для второго клиента или нового чата.',
    firstMessage: `Будь моим консультантом по уходу за кожей. Я веду уход в приложении nitea и только что подключил его к тебе: ты можешь читать записи и писать в них.

Прежде чем что-то советовать:
1. Вызови get_profile, get_routine, list_products и get_settings. Не спрашивай меня о том, что уже можешь прочитать.
2. Спроси, на каком языке вести записи, и скажи, что показывает get_settings. Если язык нужно поменять — вызови set_settings. Всё, что пишешь дальше — средства, шаги, причины, заметки — на этом языке; название средства остаётся как на упаковке.
3. Расспроси меня, по несколько вопросов за раз, в таком порядке: тип кожи и как она ведёт себя в течение дня; поставленные диагнозы и любые препараты, включая внутренние; что беспокоит сильнее всего и как давно; на что была плохая реакция; что уже есть, с названиями; город, климат и сколько солнца; сколько времени может занимать уход.
4. Записывай мои ответы в приложение по ходу: update_profile для фактов, add_product для того, что есть, всегда с причиной.
5. Только после этого собери уход через add_step: утро, вечер, по необходимости. Для каждого шага скажи, зачем он, какого результата ждать и через сколько недель о нём можно судить.
6. Прямо скажи, на что нельзя ответить словами, и что требует врача, а не консультанта.

Правила:
- Спрашивай, а не предполагай. Открытый вопрос лучше выдуманного факта.
- Не выдумывай состав INCI, концентрацию или диагноз.
- Вводи по одному активу за раз и говори, какой именно.
- Отвечай на том языке, на котором я пишу.`,
  },

  review: {
    copyLink: 'Скопировать ссылку',
    where: 'Куда добавляете?',
    openSettingsOf: (client: string) => `Открыть настройки ${client}`,
    openWith: (client: string) => `Открыть ${client} с промптом`,
    clients: {
      claude: {
        label: 'Claude',
        steps: [
          'Откройте «Настройки», перейдите в «Коннекторы».',
          'Нажмите «Добавить свой коннектор».',
          'Вставьте ссылку в поле URL, имя любое, нажмите «Добавить».',
        ],
      },
      chatgpt: {
        label: 'ChatGPT',
        steps: [
          'Откройте «Настройки», в разделе «Безопасность и вход» включите режим разработчика.',
          'Откройте страницу плагинов и нажмите «+» справа сверху.',
          'Вставьте ссылку, авторизация «No Auth», отметьте подтверждение, нажмите «Создать».',
        ],
      },
      perplexity: {
        label: 'Perplexity',
        steps: [
          'Откройте «Настройки», перейдите в «Коннекторы».',
          'Нажмите «+ Custom connector» и выберите «Remote».',
          'Вставьте ссылку в «MCP Server URL», авторизация «None», отметьте подтверждение, нажмите «Добавить».',
        ],
        note: 'Свой коннектор работает только с Perplexity Pro, Max или Enterprise.',
      },
    },
    dueNever: 'ИИ ещё не открывал записи.',
    dueSince: (ago: string) => `ИИ читал записи ${ago} назад.`,
    dueFresh: 'ИИ читал записи на этой неделе.',
    promptNote:
      'В промпте только инструкции. Ни данных, ни ключа — ничего вашего не попадёт в чужую историю переписки.',
    prompt: `Ты разбираешь мой уход за кожей как внимательный консультант. Я веду его в приложении nitea, ты дотягиваешься до него через коннектор nitea.

Сделай по порядку:
1. Вызови get_profile и get_settings, чтобы понимать, о ком читаешь.
2. Вызови get_export за последние шесть недель, затем get_flags и get_reactions.
3. Скажи, что на самом деле происходило: следование уходу, что пропущено и почему, что говорят чек-ины.
4. Назови не больше трёх вещей, которые стоит поменять, каждую с причиной из данных и способом через четыре недели понять, сработало ли.
5. Прямо скажи, на что данные не отвечают и что требует врача, а не консультанта.

Правила:
- Не выдумывай состав INCI, концентрацию или диагноз.
- Продукт младше четырёх недель ещё адаптируется, о нём судить рано.
- Всё, что записываешь в приложение, — на языке, который показывает get_settings. Менять его только спросив, через set_settings.
- Меняй по одной вещи за раз и говори, по какой именно.
- Отвечай на том языке, на котором я пишу.

Если коннектора nitea нет, читай приложенный файл выгрузки.`,
  },

  attention: {
    title: 'Внимание',
    hint: (count: string) => `К решению: ${count}. Уберите причину — строка исчезнет.`,
    emptyTitle: 'Всё разобрано',
    emptyHint:
      'Ничего не ждёт решения. Строки появятся сами, когда пройдёт срок или ИИ что-то запишет.',
    close: 'Закрыть',
    sorted: 'Разобрался',
    open: 'Открыть',
    nothingMarkedRead: 'Здесь ничего не отмечается прочитанным. Строка уходит вместе с причиной.',
    expiredOn: (date: string, inRoutine: boolean) =>
      inRoutine ? `истёк ${date}, всё ещё в уходе` : `истёк ${date}`,
    expiredBadge: 'истёк',
    empty: (inRoutine: boolean) => (inRoutine ? 'пусто, всё ещё в уходе' : 'пусто'),
    emptyBadge: 'пусто',
    expiresIn: (days: string) => `истекает через ${days} — просто чтобы знать`,
    raisedOn: (date: string) => `отмечено ${date}`,
  },

  notes: {
    title: 'Заметки ИИ',
    hint: 'Развенчанные мифы и что стоит знать — делать ничего не нужно',
    emptyTitle: 'Пока ничего не записано',
  },

  errors: {
    atMost: (value: string) => `не больше ${value}`,
    atLeast: (value: string) => `не меньше ${value}`,
    missingField: 'поле не заполнено',
    badFormat: 'формат не подходит',
  },

  calendar: {
    weekdayLetters: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    weekdays: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    weekdaysShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    months: [
      'январь',
      'февраль',
      'март',
      'апрель',
      'май',
      'июнь',
      'июль',
      'август',
      'сентябрь',
      'октябрь',
      'ноябрь',
      'декабрь',
    ],
    monthsGenitive: [
      'января',
      'февраля',
      'марта',
      'апреля',
      'мая',
      'июня',
      'июля',
      'августа',
      'сентября',
      'октября',
      'ноября',
      'декабря',
    ],
  },

  frequency: {
    and: ' и ',
    separator: ', ',
    daily: 'ежедневно',
    byWeekday: 'по дням недели',
    everyOtherDay: 'через день',
    everyNDays: (days: string) => `каждые ${days}`,
    weekly: 'раз в неделю',
    asNeeded: 'по необходимости',
  },

  cities: {
    'Africa/Abidjan': 'Абиджан',
    'Africa/Accra': 'Аккра',
    'Africa/Addis_Ababa': 'Аддис-Абеба',
    'Africa/Algiers': 'Алжир',
    'Africa/Asmera': 'Асмэра',
    'Africa/Bamako': 'Бамако',
    'Africa/Bangui': 'Банги',
    'Africa/Banjul': 'Банжул',
    'Africa/Bissau': 'Бисау',
    'Africa/Blantyre': 'Блантайр',
    'Africa/Brazzaville': 'Браззавиль',
    'Africa/Bujumbura': 'Бужумбура',
    'Africa/Cairo': 'Каир',
    'Africa/Casablanca': 'Касабланка',
    'Africa/Ceuta': 'Сеута',
    'Africa/Conakry': 'Конакри',
    'Africa/Dakar': 'Дакар',
    'Africa/Dar_es_Salaam': 'Дар-эс-Салам',
    'Africa/Djibouti': 'Джибути',
    'Africa/Douala': 'Дуала',
    'Africa/El_Aaiun': 'Эль-Аюн',
    'Africa/Freetown': 'Фритаун',
    'Africa/Gaborone': 'Габороне',
    'Africa/Harare': 'Хараре',
    'Africa/Johannesburg': 'Йоханнесбург',
    'Africa/Juba': 'Джуба',
    'Africa/Kampala': 'Кампала',
    'Africa/Khartoum': 'Хартум',
    'Africa/Kigali': 'Кигали',
    'Africa/Kinshasa': 'Киншаса',
    'Africa/Lagos': 'Лагос',
    'Africa/Libreville': 'Либревиль',
    'Africa/Lome': 'Ломе',
    'Africa/Luanda': 'Луанда',
    'Africa/Lubumbashi': 'Лубумбаши',
    'Africa/Lusaka': 'Лусака',
    'Africa/Malabo': 'Малабо',
    'Africa/Maputo': 'Мапуту',
    'Africa/Maseru': 'Масеру',
    'Africa/Mbabane': 'Мбабане',
    'Africa/Mogadishu': 'Могадишо',
    'Africa/Monrovia': 'Монровия',
    'Africa/Nairobi': 'Найроби',
    'Africa/Ndjamena': 'Нджамена',
    'Africa/Niamey': 'Ниамей',
    'Africa/Nouakchott': 'Нуакшот',
    'Africa/Ouagadougou': 'Уагадугу',
    'Africa/Porto-Novo': 'Порто-Ново',
    'Africa/Sao_Tome': 'Сан-Томе',
    'Africa/Tripoli': 'Триполи',
    'Africa/Tunis': 'Тунис',
    'Africa/Windhoek': 'Виндхук',
    'America/Adak': 'Адак',
    'America/Anchorage': 'Анкоридж',
    'America/Anguilla': 'Ангилья',
    'America/Antigua': 'Антигуа',
    'America/Araguaina': 'Арагуаина',
    'America/Argentina/La_Rioja': 'Ла-Риоха',
    'America/Argentina/Rio_Gallegos': 'Рио-Гальегос',
    'America/Argentina/Salta': 'Сальта',
    'America/Argentina/San_Juan': 'Сан-Хуан',
    'America/Argentina/San_Luis': 'Сан-Луис',
    'America/Argentina/Tucuman': 'Тукуман',
    'America/Argentina/Ushuaia': 'Ушуая',
    'America/Aruba': 'Аруба',
    'America/Asuncion': 'Асунсьон',
    'America/Bahia': 'Баия',
    'America/Bahia_Banderas': 'Баия-де-Бандерас',
    'America/Barbados': 'Барбадос',
    'America/Belem': 'Белен',
    'America/Belize': 'Белиз',
    'America/Blanc-Sablon': 'Бланк-Саблон',
    'America/Boa_Vista': 'Боа-Виста',
    'America/Bogota': 'Богота',
    'America/Boise': 'Бойсе',
    'America/Buenos_Aires': 'Буэнос-Айрес',
    'America/Cambridge_Bay': 'Кеймбридж-Бей',
    'America/Campo_Grande': 'Кампу-Гранди',
    'America/Cancun': 'Канкун',
    'America/Caracas': 'Каракас',
    'America/Catamarca': 'Катамарка',
    'America/Cayenne': 'Кайенна',
    'America/Cayman': 'Острова Кайман',
    'America/Chicago': 'Чикаго',
    'America/Chihuahua': 'Чиуауа',
    'America/Ciudad_Juarez': 'Сьюдад-Хуарес',
    'America/Coral_Harbour': 'Корал-Харбор',
    'America/Cordoba': 'Кордова',
    'America/Costa_Rica': 'Коста-Рика',
    'America/Coyhaique': 'Койайке',
    'America/Creston': 'Крестон',
    'America/Cuiaba': 'Куяба',
    'America/Curacao': 'Кюрасао',
    'America/Danmarkshavn': 'Денмарксхавн',
    'America/Dawson': 'Доусон',
    'America/Dawson_Creek': 'Доусон-Крик',
    'America/Denver': 'Денвер',
    'America/Detroit': 'Детройт',
    'America/Dominica': 'Доминика',
    'America/Edmonton': 'Эдмонтон',
    'America/Eirunepe': 'Эйрунепе',
    'America/El_Salvador': 'Сальвадор',
    'America/Fort_Nelson': 'Форт Нельсон',
    'America/Fortaleza': 'Форталеза',
    'America/Glace_Bay': 'Глейс-Бей',
    'America/Godthab': 'Нуук',
    'America/Goose_Bay': 'Гус-Бей',
    'America/Grand_Turk': 'Гранд-Терк',
    'America/Grenada': 'Гренада',
    'America/Guadeloupe': 'Гваделупа',
    'America/Guatemala': 'Гватемала',
    'America/Guayaquil': 'Гуаякиль',
    'America/Guyana': 'Гайана',
    'America/Halifax': 'Галифакс',
    'America/Havana': 'Гавана',
    'America/Hermosillo': 'Эрмосильо',
    'America/Indiana/Knox': 'Нокс, Индиана',
    'America/Indiana/Marengo': 'Маренго, Индиана',
    'America/Indiana/Petersburg': 'Питерсберг, Индиана',
    'America/Indiana/Tell_City': 'Телл-Сити',
    'America/Indiana/Vevay': 'Вевей, Индиана',
    'America/Indiana/Vincennes': 'Винсеннес',
    'America/Indiana/Winamac': 'Уинамак',
    'America/Indianapolis': 'Индианаполис',
    'America/Inuvik': 'Инувик',
    'America/Iqaluit': 'Икалуит',
    'America/Jamaica': 'Ямайка',
    'America/Jujuy': 'Жужуй',
    'America/Juneau': 'Джуно',
    'America/Kentucky/Monticello': 'Монтиселло, Кентукки',
    'America/Kralendijk': 'Кралендейк',
    'America/La_Paz': 'Ла-Пас',
    'America/Lima': 'Лима',
    'America/Los_Angeles': 'Лос-Анджелес',
    'America/Louisville': 'Луисвилл',
    'America/Lower_Princes': 'Лоуэр-Принс-Куотер',
    'America/Maceio': 'Масейо',
    'America/Managua': 'Манагуа',
    'America/Manaus': 'Манаус',
    'America/Marigot': 'Мариго',
    'America/Martinique': 'Мартиника',
    'America/Matamoros': 'Матаморос',
    'America/Mazatlan': 'Масатлан',
    'America/Mendoza': 'Мендоса',
    'America/Menominee': 'Меномини',
    'America/Merida': 'Мерида',
    'America/Metlakatla': 'Метлакатла',
    'America/Mexico_City': 'Мехико',
    'America/Miquelon': 'Микелон',
    'America/Moncton': 'Монктон',
    'America/Monterrey': 'Монтеррей',
    'America/Montevideo': 'Монтевидео',
    'America/Montserrat': 'Монтсеррат',
    'America/Nassau': 'Нассау',
    'America/New_York': 'Нью-Йорк',
    'America/Nome': 'Ном',
    'America/Noronha': 'Норонья',
    'America/North_Dakota/Beulah': 'Бойла, Северная Дакота',
    'America/North_Dakota/Center': 'Центр, Северная Дакота',
    'America/North_Dakota/New_Salem': 'Нью-Сейлем, Северная Дакота',
    'America/Ojinaga': 'Охинага',
    'America/Panama': 'Панама',
    'America/Paramaribo': 'Парамарибо',
    'America/Phoenix': 'Финикс',
    'America/Port-au-Prince': 'Порт-о-Пренс',
    'America/Port_of_Spain': 'Порт-оф-Спейн',
    'America/Porto_Velho': 'Порту-Велью',
    'America/Puerto_Rico': 'Пуэрто-Рико',
    'America/Punta_Arenas': 'Пунта-Аренас',
    'America/Rankin_Inlet': 'Ранкин-Инлет',
    'America/Recife': 'Ресифи',
    'America/Regina': 'Реджайна',
    'America/Resolute': 'Резольют',
    'America/Rio_Branco': 'Риу-Бранку',
    'America/Santarem': 'Сантарен',
    'America/Santiago': 'Сантьяго',
    'America/Santo_Domingo': 'Санто-Доминго',
    'America/Sao_Paulo': 'Сан-Паулу',
    'America/Scoresbysund': 'Скорсбисунн',
    'America/Sitka': 'Ситка',
    'America/St_Barthelemy': 'Сен-Бартелеми',
    'America/St_Johns': 'Сент-Джонс',
    'America/St_Kitts': 'Сент-Китс',
    'America/St_Lucia': 'Сент-Люсия',
    'America/St_Thomas': 'Сент-Томас',
    'America/St_Vincent': 'Сент-Винсент',
    'America/Swift_Current': 'Свифт-Керрент',
    'America/Tegucigalpa': 'Тегусигальпа',
    'America/Thule': 'Туле',
    'America/Tijuana': 'Тихуана',
    'America/Toronto': 'Торонто',
    'America/Tortola': 'Тортола',
    'America/Vancouver': 'Ванкувер',
    'America/Whitehorse': 'Уайтхорс',
    'America/Winnipeg': 'Виннипег',
    'America/Yakutat': 'Якутат',
    'Antarctica/Casey': 'Кейси',
    'Antarctica/Davis': 'Дейвис',
    'Antarctica/DumontDUrville': 'Дюмон-д’Юрвиль',
    'Antarctica/Macquarie': 'Маккуори',
    'Antarctica/Mawson': 'Моусон',
    'Antarctica/McMurdo': 'Мак-Мердо',
    'Antarctica/Palmer': 'Палмер',
    'Antarctica/Rothera': 'Ротера',
    'Antarctica/Syowa': 'Сёва',
    'Antarctica/Troll': 'Тролль',
    'Antarctica/Vostok': 'Восток',
    'Arctic/Longyearbyen': 'Лонгйир',
    'Asia/Aden': 'Аден',
    'Asia/Almaty': 'Алматы',
    'Asia/Amman': 'Амман',
    'Asia/Anadyr': 'Анадырь',
    'Asia/Aqtau': 'Актау',
    'Asia/Aqtobe': 'Актобе',
    'Asia/Ashgabat': 'Ашхабад',
    'Asia/Atyrau': 'Атырау',
    'Asia/Baghdad': 'Багдад',
    'Asia/Bahrain': 'Бахрейн',
    'Asia/Baku': 'Баку',
    'Asia/Bangkok': 'Бангкок',
    'Asia/Barnaul': 'Барнаул',
    'Asia/Beirut': 'Бейрут',
    'Asia/Bishkek': 'Бишкек',
    'Asia/Brunei': 'Бруней',
    'Asia/Calcutta': 'Калькутта',
    'Asia/Chita': 'Чита',
    'Asia/Colombo': 'Коломбо',
    'Asia/Damascus': 'Дамаск',
    'Asia/Dhaka': 'Дакка',
    'Asia/Dili': 'Дили',
    'Asia/Dubai': 'Дубай',
    'Asia/Dushanbe': 'Душанбе',
    'Asia/Famagusta': 'Фамагуста',
    'Asia/Gaza': 'Газа',
    'Asia/Hebron': 'Хеврон',
    'Asia/Hong_Kong': 'Гонконг',
    'Asia/Hovd': 'Ховд',
    'Asia/Irkutsk': 'Иркутск',
    'Asia/Jakarta': 'Джакарта',
    'Asia/Jayapura': 'Джаяпура',
    'Asia/Jerusalem': 'Иерусалим',
    'Asia/Kabul': 'Кабул',
    'Asia/Kamchatka': 'Петропавловск-Камчатский',
    'Asia/Karachi': 'Карачи',
    'Asia/Katmandu': 'Катманду',
    'Asia/Khandyga': 'Хандыга',
    'Asia/Krasnoyarsk': 'Красноярск',
    'Asia/Kuala_Lumpur': 'Куала-Лумпур',
    'Asia/Kuching': 'Кучинг',
    'Asia/Kuwait': 'Кувейт',
    'Asia/Macau': 'Макао',
    'Asia/Magadan': 'Магадан',
    'Asia/Makassar': 'Макасар',
    'Asia/Manila': 'Манила',
    'Asia/Muscat': 'Маскат',
    'Asia/Nicosia': 'Никосия',
    'Asia/Novokuznetsk': 'Новокузнецк',
    'Asia/Novosibirsk': 'Новосибирск',
    'Asia/Omsk': 'Омск',
    'Asia/Oral': 'Уральск',
    'Asia/Phnom_Penh': 'Пномпень',
    'Asia/Pontianak': 'Понтианак',
    'Asia/Pyongyang': 'Пхеньян',
    'Asia/Qatar': 'Катар',
    'Asia/Qostanay': 'Костанай',
    'Asia/Qyzylorda': 'Кызылорда',
    'Asia/Rangoon': 'Янгон',
    'Asia/Riyadh': 'Эр-Рияд',
    'Asia/Saigon': 'Хошимин',
    'Asia/Sakhalin': 'о-в Сахалин',
    'Asia/Samarkand': 'Самарканд',
    'Asia/Seoul': 'Сеул',
    'Asia/Shanghai': 'Шанхай',
    'Asia/Singapore': 'Сингапур',
    'Asia/Srednekolymsk': 'Среднеколымск',
    'Asia/Taipei': 'Тайбэй',
    'Asia/Tashkent': 'Ташкент',
    'Asia/Tbilisi': 'Тбилиси',
    'Asia/Tehran': 'Тегеран',
    'Asia/Thimphu': 'Тхимпху',
    'Asia/Tokyo': 'Токио',
    'Asia/Tomsk': 'Томск',
    'Asia/Ulaanbaatar': 'Улан-Батор',
    'Asia/Urumqi': 'Урумчи',
    'Asia/Ust-Nera': 'Усть-Нера',
    'Asia/Vientiane': 'Вьентьян',
    'Asia/Vladivostok': 'Владивосток',
    'Asia/Yakutsk': 'Якутск',
    'Asia/Yekaterinburg': 'Екатеринбург',
    'Asia/Yerevan': 'Ереван',
    'Atlantic/Azores': 'Азорские о-ва',
    'Atlantic/Bermuda': 'Бермудские о-ва',
    'Atlantic/Canary': 'Канарские о-ва',
    'Atlantic/Cape_Verde': 'Кабо-Верде',
    'Atlantic/Faeroe': 'Фарерские о-ва',
    'Atlantic/Madeira': 'Мадейра',
    'Atlantic/Reykjavik': 'Рейкьявик',
    'Atlantic/South_Georgia': 'Южная Георгия',
    'Atlantic/St_Helena': 'о-в Святой Елены',
    'Atlantic/Stanley': 'Стэнли',
    'Australia/Adelaide': 'Аделаида',
    'Australia/Brisbane': 'Брисбен',
    'Australia/Broken_Hill': 'Брокен-Хилл',
    'Australia/Darwin': 'Дарвин',
    'Australia/Eucla': 'Юкла',
    'Australia/Hobart': 'Хобарт',
    'Australia/Lindeman': 'Линдеман',
    'Australia/Lord_Howe': 'Лорд-Хау',
    'Australia/Melbourne': 'Мельбурн',
    'Australia/Perth': 'Перт',
    'Australia/Sydney': 'Сидней',
    'Europe/Amsterdam': 'Амстердам',
    'Europe/Andorra': 'Андорра',
    'Europe/Astrakhan': 'Астрахань',
    'Europe/Athens': 'Афины',
    'Europe/Belgrade': 'Белград',
    'Europe/Berlin': 'Берлин',
    'Europe/Bratislava': 'Братислава',
    'Europe/Brussels': 'Брюссель',
    'Europe/Bucharest': 'Бухарест',
    'Europe/Budapest': 'Будапешт',
    'Europe/Busingen': 'Бюзинген-на-Верхнем-Рейне',
    'Europe/Chisinau': 'Кишинев',
    'Europe/Copenhagen': 'Копенгаген',
    'Europe/Dublin': 'Дублин',
    'Europe/Gibraltar': 'Гибралтар',
    'Europe/Guernsey': 'Гернси',
    'Europe/Helsinki': 'Хельсинки',
    'Europe/Isle_of_Man': 'о-в Мэн',
    'Europe/Istanbul': 'Стамбул',
    'Europe/Jersey': 'Джерси',
    'Europe/Kaliningrad': 'Калининград',
    'Europe/Kiev': 'Киев',
    'Europe/Kirov': 'Киров',
    'Europe/Lisbon': 'Лиссабон',
    'Europe/Ljubljana': 'Любляна',
    'Europe/London': 'Лондон',
    'Europe/Luxembourg': 'Люксембург',
    'Europe/Madrid': 'Мадрид',
    'Europe/Malta': 'Мальта',
    'Europe/Mariehamn': 'Мариехамн',
    'Europe/Minsk': 'Минск',
    'Europe/Monaco': 'Монако',
    'Europe/Moscow': 'Москва',
    'Europe/Oslo': 'Осло',
    'Europe/Paris': 'Париж',
    'Europe/Podgorica': 'Подгорица',
    'Europe/Prague': 'Прага',
    'Europe/Riga': 'Рига',
    'Europe/Rome': 'Рим',
    'Europe/Samara': 'Самара',
    'Europe/San_Marino': 'Сан-Марино',
    'Europe/Sarajevo': 'Сараево',
    'Europe/Saratov': 'Саратов',
    'Europe/Simferopol': 'Симферополь',
    'Europe/Skopje': 'Скопье',
    'Europe/Sofia': 'София',
    'Europe/Stockholm': 'Стокгольм',
    'Europe/Tallinn': 'Таллин',
    'Europe/Tirane': 'Тирана',
    'Europe/Ulyanovsk': 'Ульяновск',
    'Europe/Vaduz': 'Вадуц',
    'Europe/Vatican': 'Ватикан',
    'Europe/Vienna': 'Вена',
    'Europe/Vilnius': 'Вильнюс',
    'Europe/Volgograd': 'Волгоград',
    'Europe/Warsaw': 'Варшава',
    'Europe/Zagreb': 'Загреб',
    'Europe/Zurich': 'Цюрих',
    'Indian/Antananarivo': 'Антананариву',
    'Indian/Chagos': 'Чагос',
    'Indian/Christmas': 'о-в Рождества',
    'Indian/Cocos': 'Кокосовые о-ва',
    'Indian/Comoro': 'Коморы',
    'Indian/Kerguelen': 'Кергелен',
    'Indian/Mahe': 'Маэ',
    'Indian/Maldives': 'Мальдивы',
    'Indian/Mauritius': 'Маврикий',
    'Indian/Mayotte': 'Майотта',
    'Indian/Reunion': 'Реюньон',
    'Pacific/Apia': 'Апиа',
    'Pacific/Auckland': 'Окленд',
    'Pacific/Bougainville': 'Бугенвиль',
    'Pacific/Chatham': 'Чатем',
    'Pacific/Easter': 'о-в Пасхи',
    'Pacific/Efate': 'Эфате',
    'Pacific/Enderbury': 'о-в Эндербери',
    'Pacific/Fakaofo': 'Факаофо',
    'Pacific/Fiji': 'Фиджи',
    'Pacific/Funafuti': 'Фунафути',
    'Pacific/Galapagos': 'Галапагосские о-ва',
    'Pacific/Gambier': 'о-ва Гамбье',
    'Pacific/Guadalcanal': 'Гуадалканал',
    'Pacific/Guam': 'Гуам',
    'Pacific/Honolulu': 'Гонолулу',
    'Pacific/Kiritimati': 'Киритимати',
    'Pacific/Kosrae': 'Косрае',
    'Pacific/Kwajalein': 'Кваджалейн',
    'Pacific/Majuro': 'Маджуро',
    'Pacific/Marquesas': 'Маркизские о-ва',
    'Pacific/Midway': 'о-ва Мидуэй',
    'Pacific/Nauru': 'Науру',
    'Pacific/Niue': 'Ниуэ',
    'Pacific/Norfolk': 'Норфолк',
    'Pacific/Noumea': 'Нумеа',
    'Pacific/Pago_Pago': 'Паго-Паго',
    'Pacific/Palau': 'Палау',
    'Pacific/Pitcairn': 'Питкэрн',
    'Pacific/Ponape': 'Понпеи',
    'Pacific/Port_Moresby': 'Порт-Морсби',
    'Pacific/Rarotonga': 'Раротонга',
    'Pacific/Saipan': 'Сайпан',
    'Pacific/Tahiti': 'Таити',
    'Pacific/Tarawa': 'Тарава',
    'Pacific/Tongatapu': 'Тонгатапу',
    'Pacific/Truk': 'Трук',
    'Pacific/Wake': 'Уэйк',
    'Pacific/Wallis': 'Уоллис',
    UTC: 'UTC',
  } as Record<string, string>,

  vocabulary: {
    checkinOption: {
      better: 'лучше',
      'the same': 'так же',
      worse: 'хуже',
      none: 'нет',
      '1-2': '1–2',
      '3-5': '3–5',
      '6+': '6 и больше',
      'a little': 'немного',
      'a lot': 'сильно',
      noticeable: 'заметно',
      all: 'всё',
      'almost all': 'почти всё',
      'about half': 'примерно половину',
      'almost none': 'почти ничего',
      'clearly better': 'заметно лучше',
      'a little better': 'немного лучше',
      'no change': 'без перемен',
      forehead: 'лоб',
      nose: 'нос',
      cheeks: 'щёки',
      chin: 'подбородок',
      nowhere: 'нигде',
      lighter: 'светлее',
      darker: 'темнее',
      'not tracking': 'не слежу',
    },
    field: {
      name: 'название',
      brand: 'бренд',
      category: 'категория',
      texture: 'текстура',
      status: 'статус',
      remaining: 'остаток',
      purpose: 'зачем',
      notes: 'заметки',
      inciSource: 'источник INCI',
      expiresOn: 'годен до',
      openedOn: 'открыт',
      startedOn: 'начат',
      expectedEffect: 'ожидаемый эффект',
      expectedSignal: 'признак результата',
      amount: 'количество',
      slot: 'время',
      position: 'позиция',
      frequency: 'частота',
      daysOfWeek: 'дни недели',
      intervalDays: 'раз в столько дней',
      inci: 'INCI',
      actives: 'активы',
      concerns: 'для чего',
      fragrance: 'отдушка',
      essentialOils: 'эфирные масла',
      alcoholDenat: 'денатурат',
      sizeMl: 'объём, мл',
      doseMl: 'разовая доза, мл',
      paoMonths: 'месяцев после вскрытия',
      expiryUncertain: 'срок неточен',
      evaluateAfterWeeks: 'оценить через, недель',
      skinType: 'тип кожи',
      conditions: 'состояния',
      intolerances: 'непереносимость',
      medications: 'препараты',
      city: 'город',
      climate: 'климат',
    },
    action: {
      added: 'добавлено',
      removed: 'убрано',
      updated: 'изменено',
    },
    flag: {
      true: 'да',
      false: 'нет',
    },
    status: {
      active: 'активно',
      paused: 'на паузе',
      finished: 'закончилось',
      candidate: 'кандидат',
      rejected: 'отклонено',
    },

    remaining: {
      full: 'полное',
      most: 'больше половины',
      half: 'половина',
      low: 'заканчивается',
      empty: 'пусто',
    },

    category: {
      cleanser: 'очищающее средство',
      'cleansing-oil': 'гидрофильное масло',
      bar: 'твёрдое мыло',
      toner: 'тоник',
      essence: 'эссенция',
      serum: 'сыворотка',
      ampoule: 'ампула',
      moisturizer: 'увлажняющее средство',
      'gel-cream': 'гель-крем',
      cream: 'крем',
      oil: 'масло',
      sunscreen: 'солнцезащитное средство',
      'eye-care': 'уход за глазами',
      mask: 'маска',
      exfoliant: 'эксфолиант',
      'spot-treatment': 'точечное средство',
      'lip-care': 'уход за губами',
      mist: 'мист',
      other: 'другое',
    },

    active: {
      retinoid: 'ретиноиды',
      acid: 'кислоты',
      antioxidant: 'антиоксиданты',
      hydration: 'увлажнение',
      barrier: 'барьер',
      spf: 'SPF',
      soothing: 'успокаивающие',
      preservative: 'консервант',
    },

    confidence: {
      stated: 'заявлено',
      estimated: 'оценка',
      unknown: 'неизвестно',
    },

    evidence: {
      strong: 'сильные доказательства',
      moderate: 'средние доказательства',
      weak: 'слабые доказательства',
      none: 'без доказательств',
      'marketing-claim': 'маркетинговое заявление',
    },

    slot: {
      morning: 'Утро',
      evening: 'Вечер',
      weekly: 'Раз в неделю',
      'as-needed': 'По необходимости',
    },

    symptom: {
      stinging: 'пощипывание',
      burning: 'жжение',
      redness: 'покраснение',
      peeling: 'шелушение',
      dryness: 'сухость',
      itching: 'зуд',
      breakout: 'высыпания',
      swelling: 'отёк',
      hives: 'крапивница',
      other: 'другое',
    },

    severity: {
      mild: 'слабая',
      moderate: 'средняя',
      severe: 'сильная',
    },

    level: {
      info: 'заметка',
      warning: 'конфликт',
      alert: 'тревога',
    },

    source: {
      app: 'из приложения',
      mcp: 'от ИИ',
      bot: 'от бота',
      manual: 'вручную',
      import: 'из импорта',
    },

    skinType: {
      dry: 'сухая',
      normal: 'нормальная',
      combination: 'комбинированная',
      oily: 'жирная',
      sensitive: 'чувствительная',
    },

    condition: {
      acne: 'акне',
      rosacea: 'розацеа',
      'seborrheic-dermatitis': 'себорейный дерматит',
      'atopic-dermatitis': 'атопический дерматит',
      'perioral-dermatitis': 'периоральный дерматит',
      melasma: 'мелазма',
      eczema: 'экзема',
      psoriasis: 'псориаз',
    },

    concern: {
      acne: 'акне',
      'post-acne': 'постакне',
      pigmentation: 'пигментация',
      redness: 'покраснения',
      dryness: 'сухость',
      oiliness: 'жирность',
      texture: 'текстура',
      pores: 'поры',
      wrinkles: 'морщины',
      sensitivity: 'чувствительность',
      dullness: 'тусклость',
    },

    event: {
      purchased: 'куплено',
      opened: 'открыто',
      paused: 'на паузе',
      resumed: 'возобновлено',
      finished: 'закончилось',
      discarded: 'выброшено',
    },

    angle: {
      front: 'анфас',
      left: 'слева',
      right: 'справа',
      detail: 'деталь',
    },

    checkin: {
      weekly: 'недельный',
      monthly: 'месячный',
    },
  },
}
